<script setup>
// 푸터 — 반응형으로 옮긴 세 번째 섹션.
//
// 원본: Figma 홈(ver1) 4133:20362 (1920x411), 배경 #252525
//   콘텐츠 x 64~1856 (1792)
//   설명문   y 50   18px 행간 26 흰색 (두 줄)
//   메뉴     y 63   오른쪽 끝, 18px semibold, 항목 간격 50
//   구분선   y 152  1792x1 흰색 30%
//   회사정보 y 183  두 덩이(x 64 / x 517), 15px 행간 22, 줄 간격 6
//                  항목 사이 1x12 흰색 30% 구분자, 간격 10
//   소셜     y 281  30x30 네 개, 간격 24, 오른쪽 끝
//   Copyright y 291 14px 행간 20 흰색 50%
//   아래 여백 100
//
// 원본에서 바꾼 것
//   1) 절대좌표를 걷어내고 가로 줄로 담았다. 좁아지면 접힌다.
//   2) 메뉴를 useSiteFooter 로 갈아 끼우지 않고 SITE_MENU 를 바로 그린다.
//      그 함수는 퍼블리싱의 <p> 를 그 자리에서 <a> 로 바꿔 끼우는 물건이라,
//      처음부터 <a> 로 쓰는 여기서는 필요 없다. 보이는 결과는 같다.
//      (자사몰·미디어허브 푸터는 아직 그 함수를 쓴다)
//   3) 글자는 전부 원본 값 그대로 고정이다. 18 / 15 / 14px 이라 창 폭을 탈 이유가 없다.
//
// 배경은 제 것을 갖는다. 디졸브는 비전에서 끝나고 푸터는 빠져 있다(사용자 요청).
import { ref } from 'vue'
import { useDesignScale } from '@/composables/useDesignScale.js'
import { SITE_MENU } from '@/data/siteMenu.js'
// 회사정보 · 설명문 · 저작권 줄은 모바일 푸터와 같이 쓴다(2026-11-04 에 data 로 뺐다).
import { FOOTER_COPYRIGHT, FOOTER_INFO_LEFT, FOOTER_INFO_RIGHT, FOOTER_LEAD } from '@/data/footerInfo.js'
import imgSocialYouTube from '@/assets/figma/ba2e5ba040ce51d33a1623b513a3690e8a7fadfc.svg'
import imgSocialFacebook from '@/assets/figma/91db2067d123c43be29c48e9fe466bb2eff3a7a1.svg'
import imgSocialX from '@/assets/figma/66a12c4d4c88975ee20c830c0c8b6b31256fa7f9.svg'
import imgSocialInstagram from '@/assets/figma/a46be4fab735b5f3c3c3f0967ecf2961c7e444a2.svg'

// 세로 리듬 배율. 여백을 창 폭에 맞춰 줄인다 — 글자는 건드리지 않는다.
// 자세한 이유는 composables/useDesignScale.js 에 있다.
const root = ref(null)
const { scale: rhythm } = useDesignScale(root)

// 회사정보 두 덩이. 각 줄은 [앞말, 뒷말] 이고 사이에 세로 구분자가 들어간다.
const INFO_LEFT = FOOTER_INFO_LEFT
const INFO_RIGHT = FOOTER_INFO_RIGHT

const SOCIALS = [
  { key: 'youtube', src: imgSocialYouTube, label: 'YouTube' },
  { key: 'facebook', src: imgSocialFacebook, label: 'Facebook' },
  { key: 'x', src: imgSocialX, label: 'X' },
  { key: 'instagram', src: imgSocialInstagram, label: 'Instagram' },
]
</script>

<template>
  <footer
    ref="root"
    :style="{ '--r': rhythm }"
    class="relative w-full"
    data-node-id="4133:20362"
  >
    <div class="mx-auto w-full max-w-[1530px] px-[20px]">
      <!-- 설명문(왼쪽) + 메뉴(오른쪽) -->
      <div class="flex flex-wrap items-start justify-between gap-x-[50px] gap-y-[24px]">
        <p class="footer-lead" data-node-id="4133:20365">
          {{ FOOTER_LEAD[0] }}
          <br aria-hidden />
          {{ FOOTER_LEAD[1] }}
        </p>
        <nav class="flex flex-wrap gap-x-[50px] gap-y-[8px]" data-node-id="4133:20366">
          <a v-for="item in SITE_MENU" :key="item.key" :href="item.href" class="footer-menu">
            {{ item.label }}
          </a>
        </nav>
      </div>

      <div class="mt-[50px] h-px w-full bg-white/30" data-node-id="4133:20373" />

      <!-- 회사정보 두 덩이 -->
      <div class="mt-[30px] flex flex-wrap gap-x-[159px] gap-y-[12px]">
        <div v-for="(block, b) in [INFO_LEFT, INFO_RIGHT]" :key="b" class="flex flex-col gap-[6px]">
          <div v-for="(row, r) in block" :key="r" class="flex flex-wrap items-center gap-[10px]">
            <template v-for="(cell, c) in row" :key="c">
              <span v-if="c" aria-hidden class="h-[12px] w-px shrink-0 bg-white/30" />
              <span class="footer-info">{{ cell }}</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Copyright(왼쪽) + 소셜(오른쪽). 원본에서 둘 다 아래끝이 311 로 맞춰져 있다. -->
      <div class="mt-[20px] flex flex-wrap items-end justify-between gap-[20px]">
        <p class="footer-copy" data-node-id="4133:20364">
          {{ FOOTER_COPYRIGHT }}
        </p>
        <div class="flex gap-[24px]" data-node-id="4133:20402">
          <span v-for="s in SOCIALS" :key="s.key" class="block size-[30px] shrink-0">
            <img :src="s.src" :alt="s.label" class="block size-full" />
          </span>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
/* 글자는 전부 원본 값 그대로 고정이다. 창 폭을 타지 않는다. */
.footer-lead {
  margin: 0;
  font-size: 18px;
  line-height: 26px;
  color: #fff;
  word-break: keep-all;
}

.footer-menu {
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #fff;
  white-space: nowrap;
}

.footer-info {
  font-size: 15px;
  line-height: 22px;
  color: #fff;
  word-break: keep-all;
}

.footer-copy {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #fff;
  opacity: 0.5;
  white-space: nowrap;
}
</style>
